// Which of a patient's other studies are priors of a worklist row, and how they
// group into visits. The badge and the history panel both read these, so the
// count on the pill always matches the rows in the panel.
//
// A prior is a study of the same patient ID, taken on an EARLIER date, whose
// name and birth date do not contradict the row's (patient IDs get reused at
// some hospitals). Studies from the same day as the row are the current visit,
// not history. Studies on the same earlier day are one visit.

// Body regions, so "Chest PA" today counts a "THORAX" study last year as
// relevant. Matched as whole words (or the whole value with punctuation
// removed, for "L-SPINE"), never as substrings: "THORACIC SPINE" is not CSPINE.
const REGION_ALIASES = {
  CHEST: ['CHEST', 'THORAX', 'LUNG', 'RIB', 'STERNUM'],
  HEAD: ['HEAD', 'BRAIN', 'SKULL', 'FACE', 'SINUS', 'ORBIT', 'MANDIBLE'],
  NECK: ['NECK'],
  CSPINE: ['CSPINE', 'CERVICAL'],
  TSPINE: ['TSPINE', 'THORACIC', 'DORSAL'],
  LSPINE: ['LSPINE', 'LUMBAR', 'LUMBOSACRAL', 'SACRUM'],
  ABDOMEN: ['ABDOMEN', 'ABD', 'LIVER', 'KIDNEY', 'KUB'],
  PELVIS: ['PELVIS', 'HIP'],
  SHOULDER: ['SHOULDER', 'CLAVICLE'],
  ELBOW: ['ELBOW'],
  ARM: ['HUMERUS', 'FOREARM', 'ARM'],
  WRIST: ['WRIST'],
  HAND: ['HAND', 'FINGER'],
  KNEE: ['KNEE'],
  LEG: ['FEMUR', 'TIBIA', 'FIBULA', 'LEG', 'THIGH'],
  ANKLE: ['ANKLE'],
  FOOT: ['FOOT', 'FEET', 'TOE', 'HEEL'],
  EXTREMITY: ['EXTREMITY'],
};

const ALIAS_TO_REGION = Object.keys(REGION_ALIASES).reduce((acc, region) => {
  REGION_ALIASES[region].forEach((alias) => { acc[alias] = region; });
  return acc;
}, {});

const regionsOfText = (text) => {
  const value = String(text || '').toUpperCase();
  const words = value.split(/[^A-Z0-9]+/).filter(Boolean);
  const candidates = [...words, value.replace(/[^A-Z0-9]/g, '')];
  const found = new Set();
  candidates.forEach((word) => {
    const region = ALIAS_TO_REGION[word] || ALIAS_TO_REGION[word.replace(/S$/, '')];
    if (region) found.add(region);
  });
  return found;
};

// The body part tag when it names a region, else the study description.
export const regionsOf = (bodyPart, description) => {
  const fromPart = bodyPart && bodyPart !== 'N/A' ? regionsOfText(bodyPart) : new Set();
  return fromPart.size > 0 ? fromPart : regionsOfText(description);
};

const overlaps = (a, b) => {
  for (const x of a) if (b.has(x)) return true;
  return false;
};

const nameTokens = (name) => new Set(
  String(name || '').toUpperCase().split(/[\s^,.\-_/]+/).filter((t) => t.length >= 2)
);

const nameKey = (name) => Array.from(nameTokens(name)).sort().join(' ');

// False when the two records look like different people. Names must share
// two parts (given + father's name) when both have two: one shared part is
// not enough, since fathers' names repeat across unrelated patients. With
// `strict`, the whole name must match and a missing name never matches.
export const samePerson = (a, b, strict = false) => {
  if (a.birthDate && b.birthDate && a.birthDate !== b.birthDate) return false;
  const ta = nameTokens(a.name);
  const tb = nameTokens(b.name);
  if (strict) return ta.size > 0 && nameKey(a.name) === nameKey(b.name);
  if (ta.size === 0 || tb.size === 0) return true;
  let shared = 0;
  ta.forEach((t) => { if (tb.has(t)) shared += 1; });
  return shared >= Math.min(2, ta.size, tb.size);
};

// A patient ID carrying this many different names is a placeholder ("PW",
// "OUT", "out pt"), not an identifier: there, only an exact name match counts.
const SHARED_ID_NAMES = 3;

// { visits: [{ date, studies, sameRegion }], relevant } for one row, newest
// visit first. `relevant` is null when the row's own region is unknown.
export const priorVisitsFor = (row, studies) => {
  const date = row?.studyDateRaw;
  if (!date || !studies || studies.length === 0) return { visits: [], relevant: null };
  const me = {
    name: row.patientName === 'N/A' ? '' : row.patientName,
    birthDate: row.birthDateRaw || '',
  };
  const rowRegions = regionsOf(row.bodyPart, row.studyDescription);
  const names = new Set(studies.map((s) => nameKey(s.patient_name)).filter(Boolean));
  const strict = names.size >= SHARED_ID_NAMES;
  const byDate = new Map();
  studies.forEach((s) => {
    if (!s.study_date || s.study_date >= date) return;
    if (s.study_instance_uid === row.studyInstanceUID) return;
    if (!samePerson(me, { name: s.patient_name, birthDate: s.patient_birth_date }, strict)) return;
    if (!byDate.has(s.study_date)) byDate.set(s.study_date, []);
    byDate.get(s.study_date).push(s);
  });
  const known = rowRegions.size > 0;
  const visits = Array.from(byDate.entries())
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([visitDate, list]) => {
      const sorted = list.slice().sort((a, b) => (a.study_time || '').localeCompare(b.study_time || ''));
      const sameRegion = known
        ? sorted.some((s) => overlaps(regionsOf(s.body_part, s.study_description), rowRegions))
        : null;
      return { date: visitDate, studies: sorted, sameRegion };
    });
  return {
    visits,
    relevant: known ? visits.filter((v) => v.sameRegion).length : null,
  };
};

// The signed study whose report a visit's "Report" button opens: a same-region
// one when the visit has several.
export const reportStudyOf = (visit, rowRegions) => {
  const signed = visit.studies.filter((s) => s.report_status === 'submitted');
  if (signed.length === 0) return null;
  if (rowRegions && rowRegions.size > 0) {
    const same = signed.find((s) => overlaps(regionsOf(s.body_part, s.study_description), rowRegions));
    if (same) return same;
  }
  return signed[0];
};

export const visitStatus = (visit) => {
  const total = visit.studies.length;
  const signed = visit.studies.filter((s) => s.report_status === 'submitted').length;
  if (signed === total) return { label: 'Signed', tone: 'signed' };
  if (signed > 0) return { label: `${signed} of ${total} signed`, tone: 'signed' };
  if (visit.studies.some((s) => s.report_status === 'draft')) return { label: 'In Progress', tone: 'draft' };
  return { label: 'Pending', tone: 'pending' };
};

const IMPRESSION_HEADING = /^\s*impressions?\b\s*[:\-–]?\s*/i;
const MAX_IMPRESSION_CHARS = 200;

// First line of the Impression section of a report's HTML, or '' when the
// report has none.
export const firstImpressionLine = (html) => {
  if (!html) return '';
  const withBreaks = String(html)
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|tr|h[1-6])\s*>/gi, '$&\n');
  const text = new DOMParser().parseFromString(withBreaks, 'text/html').body.textContent || '';
  const lines = text.split('\n').map((l) => l.replace(/\s+/g, ' ').trim()).filter(Boolean);
  const at = lines.findIndex((l) => IMPRESSION_HEADING.test(l) && l.length < 400);
  if (at < 0) return '';
  const line = lines[at].replace(IMPRESSION_HEADING, '').trim() || lines[at + 1] || '';
  return line.length > MAX_IMPRESSION_CHARS ? `${line.slice(0, MAX_IMPRESSION_CHARS - 1).trimEnd()}…` : line;
};
